import React, { useState } from "react";
import styled from "styled-components";
import { FiDollarSign, FiLogIn, FiUserPlus } from "react-icons/fi";
import { COLORS } from "../utils/constants";

const Page = styled.div`
  align-items: center;
  background: ${COLORS.gray50};
  display: flex;
  justify-content: center;
  min-height: 100vh;
  padding: 16px;
`;

const Card = styled.div`
  background: ${COLORS.white};
  border: 1px solid ${COLORS.gray200};
  border-radius: 16px;
  max-width: 400px;
  padding: 40px;
  width: 100%;

  @media (max-width: 480px) {
    padding: 24px;
  }
`;

const LogoRow = styled.div`
  align-items: center;
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 8px;
`;

const LogoIcon = styled.div`
  align-items: center;
  background: ${COLORS.primary};
  border-radius: 12px;
  color: ${COLORS.white};
  display: flex;
  height: 48px;
  justify-content: center;
  width: 48px;
`;

const Title = styled.h1`
  color: ${COLORS.gray800};
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const Subtitle = styled.p`
  color: ${COLORS.gray500};
  font-size: 14px;
  margin: 8px 0 32px 0;
  text-align: center;
`;

const Form = styled.form`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const Label = styled.label`
  color: ${COLORS.gray600};
  font-size: 13px;
  font-weight: 500;
`;

const Input = styled.input`
  border: 1px solid ${COLORS.gray300};
  border-radius: 6px;
  font-size: 14px;
  padding: 10px 12px;
  transition: border-color 0.2s;
  width: 100%;

  &:focus {
    border-color: ${COLORS.primary};
    outline: none;
  }
`;

const SubmitButton = styled.button`
  align-items: center;
  background: ${COLORS.primary};
  border: none;
  border-radius: 8px;
  color: ${COLORS.white};
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 500;
  gap: 8px;
  justify-content: center;
  margin-top: 4px;
  padding: 12px 20px;
  transition: background 0.2s;

  &:hover {
    background: ${COLORS.primaryDark};
  }

  &:disabled {
    background: ${COLORS.gray300};
    cursor: not-allowed;
  }
`;

const ToggleLink = styled.button`
  background: none;
  border: none;
  color: ${COLORS.primary};
  cursor: pointer;
  font-size: 14px;
  margin-top: 8px;
  padding: 0;
  text-align: center;

  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMsg = styled.div`
  background: ${COLORS.dangerLight};
  border-radius: 6px;
  color: ${COLORS.danger};
  font-size: 13px;
  padding: 10px 12px;
`;

const LoginPage = (props) => {
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);

    try {
      if (mode === "register") {
        await props.onRegister(email.trim(), password, name.trim());
      } else {
        await props.onLogin(email.trim(), password);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const toggleMode = () => {
    setMode(mode === "login" ? "register" : "login");
    setError("");
  };

  return (
    <Page>
      <Card>
        <LogoRow>
          <LogoIcon>
            <FiDollarSign size={24} />
          </LogoIcon>
          <Title>Petty Cash Tracker</Title>
        </LogoRow>
        <Subtitle>
          {mode === "login"
            ? "Sign in to track expenses and receipts"
            : "Create an account to get started"}
        </Subtitle>

        {error && <ErrorMsg>{error}</ErrorMsg>}

        <Form onSubmit={handleSubmit}>
          {mode === "register" && (
            <FormGroup>
              <Label>Name</Label>
              <Input
                type="text"
                placeholder="Your name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </FormGroup>
          )}

          <FormGroup>
            <Label>Username / Email</Label>
            <Input
              type="text"
              placeholder="Username or email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </FormGroup>

          <FormGroup>
            <Label>Password</Label>
            <Input
              type="password"
              placeholder={
                mode === "register" ? "Min 6 characters" : "Your password"
              }
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              minLength={mode === "register" ? 6 : undefined}
            />
          </FormGroup>

          <SubmitButton type="submit" disabled={loading}>
            {mode === "login" ? (
              <React.Fragment>
                <FiLogIn size={16} /> {loading ? "Signing in..." : "Sign in"}
              </React.Fragment>
            ) : (
              <React.Fragment>
                <FiUserPlus size={16} />{" "}
                {loading ? "Creating account..." : "Create account"}
              </React.Fragment>
            )}
          </SubmitButton>
        </Form>

        <ToggleLink onClick={toggleMode}>
          {mode === "login"
            ? "Don't have an account? Sign up"
            : "Already have an account? Sign in"}
        </ToggleLink>
      </Card>
    </Page>
  );
};

export default LoginPage;
